import moment from "moment"
import { useCallback, useEffect, useState } from "react"
import { Button, Card, Form, InputGroup, Modal, Stack, Tab, Tabs } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useSetRecoilState } from "recoil"
import { useApi } from "../../../api"
import { SuccessDialog } from "../../../components/SuccessDialog"
import { ICargo, INftAsset, IProfile } from "../../../interfaces"
import { nftAssetSeriesDrawerAtom, nftMyAssetGiftToModalAtom, nftMyAssetReturnModalAtom } from "../../../stores/main"
import { NftMyAssetDrawer } from "./components/NftMyAssetDrawer"
import { NftMyAssetGiftToModal } from "./components/NftMyAssetGiftToModal"
import { NftMyAssetReturnModal } from "./components/NftMyAssetReturnModal"
import swal from "sweetalert";
import { NftShortcut } from "../../../components/NFT/NftShortcut"

export const NftMyAssetPage = () => {
  const projectCode = 'NFT'
  const api = useApi(projectCode)
  const { t } = useTranslation()
  const navi = useNavigate()
  const [isMounted, setIsMounted] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [showEditModel, setShowEditModel] = useState(false)
  const setShowGiftToModel = useSetRecoilState(nftMyAssetGiftToModalAtom)
  const setShowReturnModel = useSetRecoilState(nftMyAssetReturnModalAtom)
  const [sourceAssets, setSourceAssets] = useState([])
  const [nftAssets, setNftAssets] = useState([])
  const [selectedAsset, setSelectedAsset] = useState<INftAsset | null>(null)
  const [selectedSellingPrice, setSelectedSellingPrice] = useState(0)
  const [selectedSellingStatus, setSelectedSellingStatus] = useState('P')
  const [releaseToEthereum, setReleaseToEthereum] = useState(false)
  const [simpleCode, setSimpleCode] = useState<string | null>(null)
  const [receiver, setReceiver] = useState<IProfile | null>(null)
  const [keyword, setKeyword] = useState('')
  const [dueDate, setDueDate] = useState<Date>(new Date())
  const setDraw = useSetRecoilState(nftAssetSeriesDrawerAtom)

  const fetchNftAssets = useCallback(() => {
    api.getUserNftAssets((cargo: ICargo) => {
      if (cargo.returnCode === '000000') {
        setSourceAssets(cargo.info)
        setNftAssets(cargo.info)
      }
    })
  }, [api])

  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true)
      fetchNftAssets()
    }
  }, [api, fetchNftAssets, isMounted])

  const handleClose = () => {
    setReceiver(null);
    setSimpleCode(null);
    setShowEditModel(false)
    setShowGiftToModel(false)
  }

  useEffect(() => {
    if (simpleCode?.length === 10) {
      api.getPublicProfile(simpleCode, (cargo: ICargo) => {
        if (cargo.returnCode === '000000' && receiver === null) {
          setReceiver(cargo.info);
        }
      })
    }
  }, [api, receiver, simpleCode])

  const search = () => {
    setNftAssets(sourceAssets.filter((asset: INftAsset) => {
      return asset.name.includes(keyword) || asset.description.includes(keyword);
    }))
  }

  return (
    <>
      <div className="">
        <div className="m-86 px-0 px-sm-5 py-5 text-center" style={{ "background": "#fff" }}>
          <NftShortcut />
          <Form.Group controlId="formSearch" className="mt-4">
            <InputGroup className="mx-auto" style={{ maxWidth: '560px', width: '95%' }}>
              <Form.Control
                autoFocus
                type="text"
                placeholder="搜尋"
                value={keyword}
                onChange={(e) => {
                  e.preventDefault();
                  setKeyword(e.target.value);
                }}
                onKeyUp={e => {
                  if (e.key === 'Enter') {
                    search();
                  }
                }}
              />
              <InputGroup.Text className="cursor-pointer px-3" style={{ borderRadius: '0 12px 12px 0' }} onClick={search}>搜尋</InputGroup.Text>
            </InputGroup>
          </Form.Group>
        </div>
        <div className="bg-white mx-0 mx-sm-3">
          <div className="grid grid-cols-1 gap-y-2 gap-x-1 xl:gap-x-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 align-items-start">

            {
              nftAssets.filter((asset: INftAsset) => asset['image'] && asset.status !== 'A').map((asset, index) => {
                const image: string = asset['image'];
                const imageUrl = image.indexOf('http') > -1 ? image : `https://file.sup.me.uk/nft/${image}`
                return (
                  <Card key={index} border='light' className='border shadow mx-3 mb-4 rounded-md'>
                    <Card.Img variant="top" src={imageUrl} className="mx-auto" />
                    <Card.Body className="px-4">
                      <Card.Title className="my-4 text-rose-500">{asset['name']}</Card.Title>
                      <div>
                        <div className="text-gray-400 text-xs mb-3">
                          <Stack direction="horizontal" gap={1}>
                            <div className="text-sm">{`${new Intl.DateTimeFormat('ko-KR').format(new Date(asset['timelog']))}`}</div>
                            <div className="ms-auto text-sm">{t('buyingPrice')}</div>
                            <div className="vr" />
                            <div className="text-sm">{new Intl.NumberFormat('en').format(asset['buyingPrice'])}</div>
                          </Stack>
                        </div>
                        <hr className="my-3" />
                        <div className="text-gray-700">{asset['description']}</div>
                        <div className="text-gray-400 text-xs mt-4 bg-slate-200 rounded-lg p-3 text-center">{asset['nftId']}</div>
                        <div className="text-right mt-3 text-purple-800 cursor-pointer" onClick={() => {
                          navi(`/web/asset/detail/${asset['nftId']}`)
                        }}>{t('detail')}</div>
                      </div>
                    </Card.Body>
                    {asset['chainType'] === 'Star River' &&
                      <Card.Footer>
                        <Stack direction="horizontal" gap={1} className="pb-2">
                          <div className="text-sm">{t('sellingPrice')}</div>
                          <div className="vr" />
                          <div className="text-sm">{new Intl.NumberFormat('en').format(asset['sellingPrice'])}</div>
                        </Stack>
                        <Stack direction="horizontal" gap={1}>
                          <div className="btn-outline-success border-1 text-sm p-1 px-3 rounded cursor-pointer ml-auto me-2" onClick={() => {
                            setSelectedAsset(asset)
                            setShowReturnModel(true)
                          }}>返還支付</div>
                          <div className="btn-outline-success border-1 text-sm p-1 px-3 rounded cursor-pointer me-2" onClick={() => {
                            setSelectedAsset(asset)
                            if (asset['status'] === 'S') {
                              setSelectedSellingPrice(asset['sellingPrice'])
                            }
                            if (asset['status'] === 'B') {
                              setSelectedSellingPrice(asset['biddingPrice'])
                            }
                            setSelectedSellingStatus(asset['status'])
                            setShowEditModel(true)
                          }}>{t('edit')}</div>
                          <div className="btn-outline-success border-1 text-sm p-1 px-3 rounded cursor-pointer" onClick={() => {
                            setSelectedAsset(asset)
                            setShowGiftToModel(true)
                          }}>{t('giftTo')}</div>
                        </Stack>
                      </Card.Footer>
                    }
                  </Card>
                )
              })
            }
          </div>
          <Modal show={showEditModel} onHide={handleClose} aria-labelledby="contained-modal-title-vcenter" centered>
            <Modal.Header closeButton>
              <div style={{ width: '24px' }}></div>
              <Modal.Title>{selectedAsset != null && selectedAsset['name']}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Form.Label>{t('release')}</Form.Label>
                <Form.Check
                  type="switch"
                  label="Start River"
                  checked={selectedSellingStatus === 'S' || selectedSellingStatus === 'B' ? true : false}
                  onChange={(e) => {
                    setSelectedSellingStatus(e.target.checked ? 'S' : 'P')
                    setReleaseToEthereum(false)
                  }}
                />
                <Form.Check
                  type="switch"
                  label="Ethereum"
                  checked={releaseToEthereum}
                  onChange={(e) => {
                    setReleaseToEthereum(e.target.checked)
                    setSelectedSellingStatus('P')
                  }}
                />
                {(selectedSellingStatus === 'S' || selectedSellingStatus === 'B') &&
                  <div className="mt-3 p-3 bg-slate-600 rounded-lg text-white">
                    <Tabs
                      defaultActiveKey={selectedSellingStatus}
                      className="mb-3"
                      justify
                    >
                      <Tab eventKey="S" title="直購價" onClick={() => {
                        setSelectedSellingStatus('S')
                      }}>
                        <Form.Group className="mt-4 mb-3">
                          <Form.Label>{t('editSellingPrice')}</Form.Label>
                          <Form.Control
                            className="text-right"
                            type="number"
                            value={selectedSellingPrice ? Number(selectedSellingPrice) : ''}
                            onChange={(e) => {
                              setSelectedSellingPrice(Number(e.target.value))
                            }}
                            autoFocus
                          />
                        </Form.Group>
                      </Tab>
                      <Tab eventKey="B" title="競價" onClick={() => {
                        setSelectedSellingStatus('B')
                      }}>
                        <Form.Group className="mt-4 mb-3">
                          <Form.Label>{t('editBiddingPrice')}</Form.Label>
                          <Form.Control
                            className="text-right"
                            type="number"
                            value={selectedSellingPrice ? Number(selectedSellingPrice) : ''}
                            onChange={(e) => {
                              setSelectedSellingPrice(Number(e.target.value))
                            }}
                            autoFocus
                          />
                          <Form.Label className="mt-3">結標時間</Form.Label>
                          <Form.Control
                            className="text-left"
                            type="date"
                            value={moment(dueDate).format("YYYY-MM-DD")}
                            onChange={(e) => {
                              setDueDate(moment(e.target.value).toDate())
                            }}
                          />
                        </Form.Group>
                      </Tab>
                    </Tabs>
                  </div>
                }
              </Form>
            </Modal.Body>
            <Modal.Footer>
              {
                selectedAsset && selectedAsset['chainType'] === 'Star River' && !releaseToEthereum &&
                <div>
                  <Button variant="warning" disabled={isLoading} onClick={() => {
                    swal({
                      title: "是否封存",
                      icon: 'warning',
                    }).then((result) => {
                      if (result) {
                        if (selectedAsset) {
                          setLoading(true)
                          api.archiveNft(selectedAsset['nftId'], (cargo: ICargo) => {
                            setLoading(false)
                            if (cargo.returnCode === '000000') {
                              fetchNftAssets()
                              handleClose()
                              SuccessDialog({ title: "NFT已封存" })
                            }
                          })
                        }
                      }
                    });

                  }}>
                    {isLoading ? t('processing') : t('archive')}
                  </Button>
                  <span>&nbsp;</span>
                  <Button variant="primary" disabled={isLoading} onClick={() => {
                    if (selectedAsset) {
                      setLoading(true)
                      api.updateNftSellingPrice(selectedAsset['nftId'], selectedSellingStatus, selectedSellingPrice, (cargo: ICargo) => {
                        setLoading(false)
                        if (cargo.returnCode === '000000') {
                          fetchNftAssets()
                          handleClose()
                        }
                      })
                      api.makeNftBid(selectedAsset['nftId'], dueDate)
                    }
                  }}>
                    {isLoading ? t('processing') : t('saveChange')}
                  </Button>
                </div>
              }
              {
                releaseToEthereum &&
                <Button variant="primary" disabled={isLoading} onClick={() => {
                  if (selectedAsset) {

                  }
                }}>
                  {isLoading ? t('processing') : t('releaseToEthereum')}
                </Button>
              }
            </Modal.Footer>
          </Modal>
          <NftMyAssetGiftToModal selectedAsset={selectedAsset} onClose={() => {
            handleClose();
            fetchNftAssets();
          }} />
          <NftMyAssetReturnModal selectedAsset={selectedAsset} onClose={() => {
            handleClose();
            fetchNftAssets();
          }} />
          <NftMyAssetDrawer />
          <div className="p-5"></div>
        </div>
      </div>
    </>
  )
}